import { useMemo } from "react";

import { StripeProduct } from "../../schema/stripe-product";
import { getConnectedAccountsCount, getPlanTierLabelKey } from "../../utils/features";

import { PaymentInterval, PlanCardData } from "./types";

type StripeProductPrice = StripeProduct["prices"][number];

/** Credit tier highlighted as the recommended ("Most popular") plan. */
const POPULAR_CREDITS = 5000;

const isInstallmentPrice = (price: { isSemiAnnual?: boolean; isQuarterly?: boolean }) =>
	price.isSemiAnnual === true || price.isQuarterly === true;

const isPlainMonthlyPrice = (price: StripeProductPrice) =>
	price.interval === "month" && (price.intervalCount ?? 1) === 1 && !isInstallmentPrice(price);

const findPriceForInterval = (
	product: StripeProduct,
	interval: PaymentInterval
): StripeProductPrice | undefined => {
	if (interval === "semi-annual") {
		return product.prices.find((price) => price.isSemiAnnual === true);
	}

	if (interval === "quarter") {
		return product.prices.find((price) => price.isQuarterly === true);
	}

	return product.prices.find((price) => price.interval === interval && !isInstallmentPrice(price));
};

/**
 * Standard (non-discounted) monthly price per credit tier. Monthly and annual
 * variants can be separate Stripe products that share a credit volume, so the
 * strikethrough baseline is resolved across all products by credits — not just
 * within the selected product.
 */
const buildBaseMonthlyByCredits = (products: StripeProduct[]): Map<number, number> => {
	const map = new Map<number, number>();

	for (const product of products) {
		const monthly = product.prices.find(isPlainMonthlyPrice);

		if (monthly) {
			map.set(Number(product.credits), Number(monthly.monthlyPrice));
		}
	}

	return map;
};

const toPlanCardData = (
	product: StripeProduct,
	price: StripeProductPrice,
	baseMonthlyByCredits: Map<number, number>
): PlanCardData => {
	// The getStripeProducts query has no responseSchema, so numeric fields can
	// arrive as strings — coerce before comparing (mirrors the original `+credits`).
	const credits = Number(product.credits);
	const monthlyPrice = Number(price.monthlyPrice);

	return {
		productId: product.id,
		priceId: price.id,
		name: product.name,
		monthlyPrice,
		baseMonthlyPrice: baseMonthlyByCredits.get(credits) ?? monthlyPrice,
		// `price` is Stripe's unit_amount passed through in cents — the amount
		// charged per billing cycle (a whole year, 6 months, …). `annualPrice`
		// cannot be used here: it is in cents for yearly prices but in euros for
		// every other interval.
		cycleTotal: Number(price.price) / 100,
		tierLabelKey: getPlanTierLabelKey(credits),
		credits,
		costsPerCredit: Number(price.costsPerCredit),
		senders: getConnectedAccountsCount(credits),
		popular: credits === POPULAR_CREDITS,
	};
};

export interface UsePlanCardsResult {
	plans: PlanCardData[];
	getPriceId: (productId: string) => string | undefined;
}

/**
 * Maps the live Stripe products to plan cards for the active payment interval,
 * sorted by credit volume so the recommended (popular) tier sits in the middle.
 * Callers gate `interval` via `availableIntervals`, so installment intervals
 * never reach users without `allowInstallments`.
 */
export const usePlanCards = (
	stripeProducts: StripeProduct[] | undefined,
	interval: PaymentInterval
): UsePlanCardsResult => {
	const plans = useMemo<PlanCardData[]>(() => {
		if (!stripeProducts) {
			return [];
		}

		const baseMonthlyByCredits = buildBaseMonthlyByCredits(stripeProducts);

		return stripeProducts
			.map((product) => {
				const price = findPriceForInterval(product, interval);

				return price ? toPlanCardData(product, price, baseMonthlyByCredits) : undefined;
			})
			.filter((plan): plan is PlanCardData => plan !== undefined)
			.sort((a, b) => a.credits - b.credits);
	}, [stripeProducts, interval]);

	const getPriceId = (productId: string) =>
		plans.find((plan) => plan.productId === productId)?.priceId;

	return { plans, getPriceId };
};
